import type { ReactElement } from 'react';

import { cn } from '@/components/ui/cn';

// Barras agrupadas propias en CSS con tokens (so=420, D8): sin librería de gráficos. Sin
// acción, el gráfico es una imagen con un resumen (role="img"); la tabla equivalente la
// dibuja quien lo usa. Con `onPick` (G1-7) es un grupo con el mismo nombre y cada barra
// con valor es un botón con su nombre ("Terminados 28/09: 3"), fuera del orden de
// tabulación: el camino de teclado es la tabla equivalente (una imagen no puede tener
// controles dentro).

export interface ChartSeries {
  /** Identificador de la serie, para la acción y las pruebas. */
  key: string;
  name: string;
  /** Clase de color del sistema: `s1` (acento) o `s2` (tinta suave, se adapta al tema). */
  tone: 's1' | 's2';
}

export interface ChartGroup {
  /** Identificador estable del grupo (la clave del período). */
  key: string;
  label: string;
  values: readonly number[];
}

interface Props {
  label: string;
  series: readonly ChartSeries[];
  groups: readonly ChartGroup[];
  /** Barra tocada: clave del grupo y de la serie. */
  onPick?: (groupKey: string, seriesKey: string) => void;
  /** Nombre de la barra tocable: el mismo de su control equivalente en la tabla. */
  barLabel?: (groupKey: string, seriesKey: string, value: number) => string;
}

/** Con muchos grupos se rotula uno de cada `step` para que las etiquetas no se pisen,
 *  contando desde el último: el período más reciente siempre lleva su fecha (QA F1 TIE-03). */
const labelStep = (n: number): number => Math.max(1, Math.ceil(n / 8));
const labeled = (index: number, count: number, step: number): boolean =>
  (count - 1 - index) % step === 0;

function Bar({
  tone,
  height,
  testId,
  label,
  onPick,
}: {
  tone: ChartSeries['tone'];
  height: string;
  testId: string;
  label: string;
  onPick: (() => void) | undefined;
}): ReactElement {
  if (onPick === undefined) {
    return <span className={cn('chartbar', tone)} style={{ height }} aria-hidden="true" />;
  }
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-label={label}
      title={label}
      data-testid={testId}
      className={cn('chartbar', 'pick', tone)}
      style={{ height }}
      onClick={onPick}
    />
  );
}

export function BarChart({ label, series, groups, onPick, barLabel }: Props): ReactElement {
  const max = Math.max(1, ...groups.flatMap((g) => g.values));
  const step = labelStep(groups.length);
  return (
    <div className="chart">
      <div className="chartlegend" aria-hidden="true">
        {series.map((s) => (
          <span key={s.key}>
            <i className={s.tone} />
            {s.name}
          </span>
        ))}
      </div>
      <div className="chartplot" role={onPick === undefined ? 'img' : 'group'} aria-label={label}>
        {groups.map((g, gi) => (
          <div key={g.key} className="chartgroup">
            <div className="chartbars">
              {series.map((s, si) => {
                const value = g.values[si] ?? 0;
                return (
                  <Bar
                    key={s.key}
                    tone={s.tone}
                    height={`${(value / max) * 100}%`}
                    testId={`bar-${g.key}-${s.key}`}
                    label={barLabel?.(g.key, s.key, value) ?? `${s.name} ${g.label}: ${value}`}
                    onPick={
                      onPick === undefined || value === 0 ? undefined : () => onPick(g.key, s.key)
                    }
                  />
                );
              })}
            </div>
            <span className="chartlabel" aria-hidden="true">
              {labeled(gi, groups.length, step) ? g.label : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
